/**
 * /megaphone — the page. Sound is MegaphoneEngine's (public/js/megaphone/engine.js);
 * this file is the console around it:
 *
 *   source   — a file, a recorded take (the shared uploader's record button), the
 *              demo announcement, or the live microphone;
 *   sound    — six presets and seven controls; the screen draws the band the
 *              chosen sound passes and, while something plays, what comes out;
 *   listen   — the preview is the engine on an AudioContext, A/B against the
 *              original with one click, seek on the waveform;
 *   live     — a press-to-talk button: hold to speak, a short press keeps the
 *              channel open; key click, roger beep and squelch as on a radio;
 *   export   — the SAME engine rendered offline (what you hear is what you get),
 *              MP3/WAV streamed, other formats through FFmpeg, a video keeps
 *              its picture (SoundtrackSplice); a batch reuses the settings.
 *
 * Configuration and strings come from the blade as window.__MG.
 */
(function () {
'use strict';

var CFG = window.__MG || {};
var T = CFG.i18n || {};
var SLUG = 'megaphone';
var LOC = window.__LOCALE || document.documentElement.lang || 'en';

function $(id) { return document.getElementById(id); }
function ae(f, extra) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, extra || {})); } catch (e) {} }
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'error'); } catch (e) {} }
/* Clock, status and meter writes from the frame loop go through the shared
   primitives (PlayheadWritesGoThroughThePrimitiveTest); the local pair keeps the
   page alive under an older core served from cache. */
function pxWrite(e, p, v, u) { if (window.__perPixel) return window.__perPixel(e, p, v, u); e.style[p] = v + (u || '%'); }
function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }
function setText(el, s) { if (el) textWrite(el, s); }
function fmtTime(s) { s = Math.max(0, s || 0); var m = Math.floor(s / 60), x = Math.floor(s % 60); return m + ':' + (x < 10 ? '0' : '') + x; }
function fmtHz(f) {
    if (f >= 1000) {
        var k = Math.round(f / 100) / 10;
        var str = (k % 1 === 0) ? String(k) : k.toLocaleString(LOC, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        return str + ' ' + T.unit_khz;
    }
    return Math.round(f / 10) * 10 + ' ' + T.unit_hz;
}
function settingsLoad(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (e) { return d; } }
function settingsSave(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (e) {} }
function isCancel(err, sig) { try { if (window.__isUserCancel) return window.__isUserCancel(err, sig); } catch (e) {} return !!(sig && sig.aborted); }

function boot(E) {
    var root = $('mg');
    if (!root) return;

    /* ── state ────────────────────────────────────────────────────────── */
    var params = E.normalize(settingsLoad('params', E.presetParams('megaphone')));
    var ctx = null, eng = null, fxGain = null, bypass = null, master = null, analyser = null;
    var src = null;                 // {file, buffer, isVideo, name, key, trimDb, demo, peaks}
    var mode = 'file';
    var ab = 'fx';
    var P = { on: false, sched: null, tl: null, fromT: 0, startCtx: 0, raf: 0, pos: 0 };
    var L = { stream: null, node: null, gate: null, inAn: null, noise: null, noiseGain: null, ready: false, onAir: false,
              rec: null, recChunks: [], recT0: 0, recTimer: 0, presses: 0, airMs: 0, airT0: 0, starting: false };
    var X = { busy: false, ac: null, token: null };
    var fmt = settingsLoad('fmt', 'mp3');
    var kbpsChoice = settingsLoad('kbps', {}) || {};
    var curve = { from: null, to: null, t0: 0, dur: 280 };
    var loopRaf = 0;

    /* ── DOM ──────────────────────────────────────────────────────────── */
    var el = {
        screen: $('mgScreen'), scope: $('mgScope'), video: $('mgVideo'), name: $('mgScreenName'), band: $('mgScreenBand'), air: $('mgAir'),
        modeFile: $('mgModeFile'), modeLive: $('mgModeLive'), srcPanel: $('mgSrc'), livePanel: $('mgLive'),
        upload: $('mgUpload'), alt: $('mgAlt'), demo: $('mgDemo'),
        player: $('mgPlayer'), wave: $('mgWave'), play: $('mgPlay'), playIcon: $('mgPlayIcon'), pauseIcon: $('mgPauseIcon'), time: $('mgTime'),
        abFx: $('mgAbFx'), abOrig: $('mgAbOrig'),
        ptt: $('mgPtt'), pttLabel: $('mgPttLabel'), pttSub: $('mgPttSub'), ring: $('mgRing'), liveStatus: $('mgLiveStatus'), liveRow: $('mgLiveRow'),
        gong: $('mgGong'), beep: $('mgBeep'), liveRec: $('mgLiveRec'), liveRecTxt: $('mgLiveRecTxt'), sink: $('mgSink'), liveOff: $('mgLiveOff'),
        exportPanel: $('mgExport'), fmts: $('mgFmts'), qMp3: $('mgQMp3'), qOther: $('mgQOther'), kbps: $('mgKbps'), kept: $('mgKept'),
        dl: $('mgDownload'), dlTxt: $('mgDownloadTxt'), batch: $('mgBatch'), prog: $('mgProg'), progBar: $('mgProgBar'), status: $('mgStatus'), cancel: $('mgCancel'),
        presets: $('mgPresets'), presetHint: $('mgPresetHint'), custom: $('mgCustom'), reset: $('mgReset'), extras: $('mgExtras')
    };

    /* ── audio context and the graph ──────────────────────────────────── */
    function newCtx() {
        var C = window.AudioContext || window.webkitAudioContext;
        ctx = new C({ latencyHint: 'interactive' });
        eng = null;
        return ctx;
    }
    function getCtx() { if (!ctx || ctx.state === 'closed') newCtx(); return ctx; }
    function ensureGraph() {
        if (eng && eng.ctx === ctx) return;
        eng = E.build(ctx, params, { trimDb: mode === 'live' ? E.LIVE_TRIM_DB : (src ? src.trimDb : 0) });
        fxGain = ctx.createGain(); bypass = ctx.createGain(); master = ctx.createGain();
        analyser = ctx.createAnalyser(); analyser.fftSize = 2048; analyser.smoothingTimeConstant = 0.78;
        eng.output.connect(fxGain); fxGain.connect(master); bypass.connect(master);
        master.connect(analyser); master.connect(ctx.destination);
        applyAb(true);
    }
    /* The live context — running, or a fresh one if the old lost its device
       (__liveAudioCtxReady: a context whose output was taken away never resumes). */
    async function readyCtx() {
        getCtx();
        try {
            if (window.__liveAudioCtxReady) {
                var c = await window.__liveAudioCtxReady(ctx);
                if (c && c !== ctx) { ctx = c; eng = null; }
            } else if (window.__safeResume) await window.__safeResume(ctx);
        } catch (e) {}
        ensureGraph();
        return ctx;
    }

    /* ── parameters ───────────────────────────────────────────────────── */
    var saveTimer = 0;
    function applyParams(opts) {
        opts = opts || {};
        params = E.normalize(params);
        root.setAttribute('data-preset', params.preset);
        var custom = !E.matchesPreset(params);
        el.custom.hidden = !custom;
        var cards = el.presets.querySelectorAll('.mg-preset');
        for (var i = 0; i < cards.length; i++) cards[i].setAttribute('aria-checked', cards[i].getAttribute('data-p') === params.preset ? 'true' : 'false');
        setText(el.presetHint, T['hint_' + params.preset] || '');
        for (var k = 0; k < E.CTL_KEYS.length; k++) {
            var key = E.CTL_KEYS[k], inp = $('mgK_' + key), out = $('mgV_' + key), v = Math.round(params[key]);
            if (inp && +inp.value !== v) inp.value = v;
            if (inp) { var r = E.CTL_RANGE[key]; inp.style.setProperty('--range-fill', ((v - r[0]) / (r[1] - r[0]) * 100) + '%'); }
            setText(out, key === 'volume' ? v + '%' : String(v));
        }
        var xs = el.extras.querySelectorAll('[data-x]');
        for (var j = 0; j < xs.length; j++) xs[j].setAttribute('aria-pressed', params[xs[j].getAttribute('data-x')] ? 'true' : 'false');
        var pl = E.plan(params, ctx ? ctx.sampleRate : 48000);
        setText(el.name, (T['preset_' + params.preset] || '') + (custom ? ' · ' + T.preset_custom : ''));
        setText(el.band, fmtHz(pl.hp) + ' – ' + fmtHz(pl.lp));
        if (eng) { eng.set(params); if (L.noiseGain) liveNoiseLevel(); }
        startCurveMorph();
        clearTimeout(saveTimer);
        saveTimer = setTimeout(function () { settingsSave('params', params); }, 250);
        if (opts.timeline && P.on) restartAtCurrent();
    }

    el.presets.addEventListener('click', function (e) {
        var b = e.target.closest('.mg-preset');
        if (!b) return;
        var p = b.getAttribute('data-p');
        var go = function () { params = E.presetParams(p); applyParams({ timeline: true }); };
        if (window.__viewTransition && !P.on && !L.ready) window.__viewTransition(go); else go();
        ae('preset', { p: p, playing: P.on ? 1 : 0, live: L.ready ? 1 : 0 });
    });
    el.presets.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].indexOf(e.key) < 0) return;
        var order = E.PRESET_ORDER, i = order.indexOf(params.preset);
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        if (document.documentElement.dir === 'rtl' && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) dir = -dir;
        var next = order[(i + dir + order.length) % order.length];
        params = E.presetParams(next); applyParams({ timeline: true });
        var b = el.presets.querySelector('[data-p="' + next + '"]'); if (b) b.focus();
        e.preventDefault();
    });
    var knobTouched = {};
    Array.prototype.forEach.call(document.querySelectorAll('.mg-range'), function (inp) {
        inp.addEventListener('input', function () {
            params[inp.getAttribute('data-k')] = +inp.value;
            applyParams();
        });
        inp.addEventListener('change', function () {
            var k = inp.getAttribute('data-k');
            if (!knobTouched[k]) { knobTouched[k] = 1; ae('knob', { k: k, v: +inp.value, p: params.preset }); }
            if ((k === 'echo' || k === 'space') && P.on) restartAtCurrent();     // the ring-out at the end moves
        });
    });
    el.extras.addEventListener('click', function (e) {
        var b = e.target.closest('[data-x]');
        if (!b) return;
        var x = b.getAttribute('data-x');
        params[x] = !params[x];
        applyParams({ timeline: true });
        ae('extra', { x: x, on: params[x] ? 1 : 0 });
    });
    el.reset.addEventListener('click', function () { params = E.presetParams(params.preset); applyParams({ timeline: true }); ae('reset'); });

    /* ── the screen: band of the sound + what comes out ───────────────── */
    var FREQS = (function () { var n = 160, a = new Float64Array(n); for (var i = 0; i < n; i++) a[i] = 60 * Math.pow(12000 / 60, i / (n - 1)); return a; })();
    function curveNow() {
        var r = E.responseDb(params, FREQS, ctx ? ctx.sampleRate : 48000), pk = -Infinity, i;
        if (!r) return null;
        for (i = 0; i < r.length; i++) if (r[i] > pk) pk = r[i];
        for (i = 0; i < r.length; i++) r[i] -= pk;
        return r;
    }
    function startCurveMorph() {
        var to = curveNow();
        if (!to) { curve.to = null; return; }
        curve.from = curve.to ? curveAt(performance.now()) : to;
        curve.to = to; curve.t0 = performance.now();
        kick();
    }
    function curveAt(now) {
        if (!curve.to) return null;
        var k = Math.min(1, (now - curve.t0) / curve.dur);
        k = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
        if (!curve.from || k >= 1) return curve.to;
        var out = new Float32Array(curve.to.length);
        for (var i = 0; i < out.length; i++) out[i] = curve.from[i] + (curve.to[i] - curve.from[i]) * k;
        return out;
    }
    var specBuf = null;
    function drawScope(now) {
        var c = el.scope;
        if (!c || el.screen.classList.contains('has-video')) return;
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var w = Math.round(c.clientWidth * dpr), h = Math.round(c.clientHeight * dpr);
        if (!w || !h) return;
        if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
        var g = c.getContext('2d');
        g.clearRect(0, 0, w, h);
        var hue = getComputedStyle(root).getPropertyValue('--mg-hue').trim() || '38';
        var col = 'hsl(' + hue + ' 92% 62%)';
        var fx = function (f) { return Math.log(f / 60) / Math.log(12000 / 60) * w; };
        var top = h * 0.24, bot = h * 0.94, DB_MIN = -42;
        var fy = function (db) { return top + (Math.min(0, Math.max(DB_MIN, db)) / DB_MIN) * (bot - top); };
        // grid
        g.lineWidth = 1; g.strokeStyle = 'rgba(148,163,184,.12)'; g.fillStyle = 'rgba(203,213,225,.55)';
        g.font = (11 * dpr) + 'px system-ui,sans-serif'; g.textBaseline = 'bottom';
        [100, 300, 1000, 3000, 10000].forEach(function (f) {
            var x = Math.round(fx(f)) + 0.5; g.beginPath(); g.moveTo(x, top - 4 * dpr); g.lineTo(x, bot); g.stroke();
            var lab = f >= 1000 ? (f / 1000) + 'k' : String(f), tw = g.measureText(lab).width;
            g.fillText(lab, Math.min(x + 3 * dpr, w - tw - 4 * dpr), h - 2 * dpr);
        });
        [-12, -24, -36].forEach(function (db) { var y = Math.round(fy(db)) + 0.5; g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke(); });
        // live spectrum
        var live = analyser && (P.on || L.ready);
        if (live) {
            var n = analyser.frequencyBinCount;
            if (!specBuf || specBuf.length !== n) specBuf = new Float32Array(n);
            analyser.getFloatFrequencyData(specBuf);
            var sr = ctx.sampleRate, bins = 96;
            g.fillStyle = 'hsl(' + hue + ' 90% 60% / .28)';
            for (var b = 0; b < bins; b++) {
                var f0 = 60 * Math.pow(12000 / 60, b / bins), f1 = 60 * Math.pow(12000 / 60, (b + 1) / bins);
                var i0 = Math.max(1, Math.floor(f0 / sr * 2 * n)), i1 = Math.max(i0 + 1, Math.ceil(f1 / sr * 2 * n)), m = -140;
                for (var q = i0; q < i1 && q < n; q++) if (specBuf[q] > m) m = specBuf[q];
                var lvl = Math.max(0, Math.min(1, (m + 90) / 75));
                var x0 = fx(f0), x1 = fx(f1);
                g.fillRect(x0 + 0.5 * dpr, bot - lvl * (bot - top), Math.max(1, x1 - x0 - dpr), lvl * (bot - top));
            }
        }
        // the band of the sound
        var r = curveAt(now || performance.now());
        if (r) {
            g.beginPath();
            for (var i = 0; i < FREQS.length; i++) { var X2 = fx(FREQS[i]), Y = fy(r[i]); if (i) g.lineTo(X2, Y); else g.moveTo(X2, Y); }
            var grad = g.createLinearGradient(0, top, 0, bot);
            grad.addColorStop(0, 'hsl(' + hue + ' 92% 60% / .38)'); grad.addColorStop(1, 'hsl(' + hue + ' 92% 60% / 0)');
            g.save(); g.lineTo(w, bot); g.lineTo(0, bot); g.closePath(); g.fillStyle = grad; g.fill(); g.restore();
            g.beginPath();
            for (var j = 0; j < FREQS.length; j++) { var X3 = fx(FREQS[j]), Y3 = fy(r[j]); if (j) g.lineTo(X3, Y3); else g.moveTo(X3, Y3); }
            g.lineWidth = 2.2 * dpr; g.strokeStyle = col; g.shadowColor = col; g.shadowBlur = 10 * dpr; g.stroke(); g.shadowBlur = 0;
        }
    }
    /* One loop, alive only while something moves: playback, the live mic, a
       morph of the curve. A page at rest draws nothing (IdleDecorationsMoveInSteps). */
    function kick() { if (!loopRaf) loopRaf = requestAnimationFrame(frame); }
    function frame(now) {
        loopRaf = 0;
        drawScope(now);
        if (P.on) tickPlayback();
        if (L.ready) tickLive();
        var morphing = now - curve.t0 < curve.dur + 20;
        if (P.on || L.ready || morphing) kick();
    }
    if (window.ResizeObserver) new ResizeObserver(function () { kick(); drawWave(); }).observe(el.screen);

    /* ── source ───────────────────────────────────────────────────────── */
    function isVideoFile(f) {
        var t = (f && f.type) || '';
        if (/^audio\//.test(t)) return false;
        if (/^video\//.test(t)) return true;
        return /\.(mp4|m4v|mov|webm|mkv|avi|3gp|wmv|flv|mpg|mpeg|ts|mts)$/i.test((f && f.name) || '');
    }
    var loadSeq = 0;
    async function loadSource(file, how) {
        if (!file) return;
        var seq = ++loadSeq;
        stopPlayback(true);
        if (mode === 'live') setMode('file');
        el.player.hidden = true;
        el.exportPanel.hidden = true;
        setStatus('');
        src = null;
        try {
            getCtx();
            var dec;
            if (window.AudioDecoderRobust && window.AudioDecoderRobust.robust) dec = await window.AudioDecoderRobust.robust(file, ctx, { slug: SLUG });
            else dec = { buffer: await ctx.decodeAudioData(await file.arrayBuffer()), tier: 1 };
            if (seq !== loadSeq) return;
            var buf = dec && dec.buffer;
            if (!buf || !buf.length) throw Object.assign(new Error('empty'), { mgNoSound: true });
            var chans = []; for (var c = 0; c < buf.numberOfChannels; c++) chans.push(buf.getChannelData(c));
            var lvl = E.speechLevelDb(chans, buf.sampleRate);
            if (lvl <= -59.9) throw Object.assign(new Error('silent'), { mgNoSound: true });
            var key = null;
            try { key = window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null; } catch (e) {}
            src = { file: file, buffer: buf, isVideo: isVideoFile(file), name: file.name || 'audio', key: key, trimDb: E.trimFor(lvl), demo: how === 'demo', tier: dec.tier || 1 };
            if (eng) eng.setTrim(src.trimDb);
            src.peaks = peaksOf(buf, 900);
            showVideo(src.isVideo ? file : null);
            el.alt.hidden = true;
            el.player.hidden = false;
            el.exportPanel.hidden = false;
            P.pos = 0;
            setupFormats();
            drawWave();
            updateTime();
            ae('source', { how: how || 'pick', video: src.isVideo ? 1 : 0, sec: Math.round(buf.duration), tier: src.tier, lvl: Math.round(lvl) });
            try { if (window.__stage) window.__stage('source_ready'); } catch (e) {}
            if (src.isVideo || (fmt !== 'mp3' && fmt !== 'wav')) warmFFmpeg();
            if (how === 'demo') startPlay(0);
        } catch (err) {
            if (seq !== loadSeq) return;
            var msg = T.err_decode;
            /* The decoder has already spoken (a file without moov gets its own
               hint with the way to /repair-audio): a second, generic toast on top
               buries the one that says what to do. */
            var spoken = !!(err && (err.uiHintShown || err.userInformed));
            if (err && err.mgNoSound) msg = T.no_sound;
            else if (err && err.code === 'AUDIO_BUFFER_ALLOC_FAILED') msg = T.err_too_large_for_device;
            if (!spoken) {
                try { if (window.__handleError && !(err && err.mgNoSound)) window.__handleError(err, 'megaphone:decode', { say: msg }); else toast(msg); } catch (e) { toast(msg); }
            }
            ae('source_failed', { why: err && err.mgNoSound ? 'no_sound' : String((err && (err.reason || err.code || err.name)) || 'decode').slice(0, 40), spoken: spoken ? 1 : 0 });
            resetSource(true);
        }
    }
    function resetSource(keepUploader) {
        loadSeq++;
        stopPlayback(true);
        src = null;
        showVideo(null);
        el.player.hidden = true;
        el.exportPanel.hidden = true;
        el.alt.hidden = false;
        if (!keepUploader) { try { if (el.upload.__uploaderReset) el.upload.__uploaderReset(); } catch (e) {} }
    }
    function onPicked(file, source) { loadSource(file, source === 'mic_record' ? 'record' : (source || 'pick')); }
    if (window.__bindUploaderFile) window.__bindUploaderFile(el.upload, function (f, d) { onPicked(f, d && d.source); });
    else el.upload.addEventListener('tool-uploader:file', function (e) { if (e.detail && e.detail.file) onPicked(e.detail.file, e.detail.source); });
    el.upload.addEventListener('tool-uploader:clear', function () { resetSource(true); });

    var videoUrl = null;
    function showVideo(file) {
        if (videoUrl) { try { URL.revokeObjectURL(videoUrl); } catch (e) {} videoUrl = null; }
        if (!file) { el.video.hidden = true; el.video.removeAttribute('src'); el.screen.classList.remove('has-video'); kick(); return; }
        videoUrl = URL.createObjectURL(file);
        el.video.src = videoUrl; el.video.hidden = false; el.screen.classList.add('has-video');
    }

    el.demo.addEventListener('click', async function () {
        getCtx();
        readyCtx();                                   // inside the gesture: the demo plays itself once loaded
        el.demo.disabled = true;
        try {
            var r = await fetch(CFG.demo, { credentials: 'same-origin' });
            if (!r.ok) throw new Error('demo ' + r.status);
            var b = await r.blob();
            var f = new File([b], (T.demo_name || 'demo') + '.mp3', { type: 'audio/mpeg' });
            if (el.upload.__uploaderInjectFile) el.upload.__uploaderInjectFile(f, 'demo'); else loadSource(f, 'demo');
            ae('demo');
        } catch (e) { toast(T.err_demo); }
        finally { el.demo.disabled = false; }
    });

    /* ── waveform ─────────────────────────────────────────────────────── */
    function peaksOf(buf, n) {
        var ch = buf.getChannelData(0), ch2 = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
        var out = new Float32Array(n), step = Math.max(1, Math.floor(ch.length / n)), mx = 0;
        for (var i = 0; i < n; i++) {
            var a = 0, s = i * step, e = Math.min(ch.length, s + step);
            for (var j = s; j < e; j += 4) { var v = Math.abs(ch[j]); if (ch2) v = Math.max(v, Math.abs(ch2[j])); if (v > a) a = v; }
            out[i] = a; if (a > mx) mx = a;
        }
        if (mx > 0) for (var k = 0; k < n; k++) out[k] = Math.sqrt(out[k] / mx);
        return out;
    }
    function voiceFrac() {
        if (!src) return 0;
        var vt = P.tl ? P.pos - P.tl.voiceAt : P.pos;
        return Math.max(0, Math.min(1, vt / src.buffer.duration));
    }
    function drawWave() {
        var c = el.wave;
        if (!c || !src || el.player.hidden) return;
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var w = Math.round(c.clientWidth * dpr), h = Math.round(c.clientHeight * dpr);
        if (!w || !h) return;
        if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
        var g = c.getContext('2d'); g.clearRect(0, 0, w, h);
        var hue = getComputedStyle(root).getPropertyValue('--mg-hue').trim() || '38';
        var bw = Math.max(2, Math.round(2 * dpr)), gap = Math.max(1, Math.round(dpr)), bars = Math.floor(w / (bw + gap)), mid = h / 2;
        var prog = voiceFrac(), pk = src.peaks;
        for (var i = 0; i < bars; i++) {
            var v = pk[Math.min(pk.length - 1, Math.floor(i / bars * pk.length))];
            var bh = Math.max(dpr, v * (h * 0.42));
            var x = i * (bw + gap);
            g.fillStyle = (x / w) <= prog ? 'hsl(' + hue + ' 90% 60%)' : 'rgba(148,163,184,.38)';
            g.fillRect(x, mid - bh, bw, bh * 2);
        }
        if (prog > 0 && prog < 1) { g.fillStyle = 'hsl(' + hue + ' 95% 70%)'; g.fillRect(Math.round(prog * w) - dpr, 0, 2 * dpr, h); }
        el.wave.setAttribute('aria-valuenow', String(Math.round(prog * 100)));
    }
    function updateTime() {
        if (!src) return;
        var d = src.buffer.duration, v = Math.max(0, Math.min(d, P.tl ? P.pos - P.tl.voiceAt : P.pos));
        setText(el.time, fmtTime(v) + ' / ' + fmtTime(d));
    }
    function seekFrac(frac) {
        if (!src) return;
        frac = Math.max(0, Math.min(1, frac));
        var tl = timelineFor(src);
        var t = frac <= 0.0005 ? 0 : tl.voiceAt + frac * src.buffer.duration;
        if (P.on) startPlay(t); else { P.pos = t; P.tl = tl; drawWave(); updateTime(); syncVideo(t, false); }
    }
    var waveDrag = false;
    el.wave.addEventListener('pointerdown', function (e) {
        if (e.button !== 0) return;
        waveDrag = true; try { el.wave.setPointerCapture(e.pointerId); } catch (x) {}
        var r = el.wave.getBoundingClientRect(); seekFrac((e.clientX - r.left) / r.width);
    });
    el.wave.addEventListener('pointermove', function (e) {
        if (!waveDrag || P.on) return;                 // while playing a drag would restart the sound on every move
        var r = el.wave.getBoundingClientRect(); seekFrac((e.clientX - r.left) / r.width);
    });
    el.wave.addEventListener('pointerup', function () { waveDrag = false; });
    el.wave.addEventListener('pointercancel', function () { waveDrag = false; });
    el.wave.addEventListener('keydown', function (e) {
        if (!src) return;
        var d = src.buffer.duration, f = voiceFrac(), step = Math.min(0.05, 2 / d);
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') seekFrac(f + step);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') seekFrac(f - step);
        else if (e.key === 'Home') seekFrac(0);
        else if (e.key === 'End') seekFrac(0.999);
        else if (e.key === ' ') { togglePlay(); }
        else return;
        e.preventDefault();
    });

    /* ── playback ─────────────────────────────────────────────────────── */
    function timelineFor(s) { return E.timeline({ params: params, duration: s.buffer.duration, sampleRate: s.buffer.sampleRate, extend: !s.isVideo }); }
    function stopSources() { if (P.sched) { P.sched.stop(); P.sched = null; } }
    async function startPlay(fromT) {
        if (!src) return;
        if (mode === 'live') return;
        await readyCtx();
        if (!src) return;
        stopSources();
        eng.setTrim(src.trimDb);
        var tl = timelineFor(src);
        if (fromT >= tl.total - 0.05) fromT = 0;
        var when = ctx.currentTime + 0.04;
        P.sched = E.schedule(ctx, eng, tl, { voice: src.buffer, voiceTap: bypass }, fromT, when);
        P.tl = tl; P.fromT = fromT; P.startCtx = when; P.on = true; P.pos = fromT;
        syncVideo(fromT, true);
        setPlayIcon(true);
        kick();
        if (!startPlay.once) { startPlay.once = 1; ae('listen', { p: params.preset }); }
    }
    function stopPlayback(reset) {
        stopSources();
        if (P.on) P.on = false;
        if (reset) { P.pos = 0; P.tl = null; }
        try { el.video.pause(); } catch (e) {}
        setPlayIcon(false);
        drawWave(); updateTime(); kick();
    }
    function restartAtCurrent() {
        if (!P.on || !src) return;
        var v = Math.max(0, P.pos - (P.tl ? P.tl.voiceAt : 0));
        var tl = timelineFor(src);
        startPlay(v <= 0.01 ? 0 : tl.voiceAt + v);
    }
    function tickPlayback() {
        P.pos = P.fromT + Math.max(0, ctx.currentTime - P.startCtx);
        if (P.pos >= P.tl.total) { stopPlayback(true); return; }
        drawWave(); updateTime();
        if (src.isVideo && !el.video.paused && Math.abs(el.video.currentTime - (P.pos - P.tl.voiceAt)) > 0.25) syncVideo(P.pos, true);
    }
    function syncVideo(t, playing) {
        if (!src || !src.isVideo) return;
        var vt = Math.max(0, t - (P.tl ? P.tl.voiceAt : 0));
        try {
            if (Math.abs(el.video.currentTime - vt) > 0.05) el.video.currentTime = vt;
            if (playing) { var pr = el.video.play(); if (pr && pr.catch) pr.catch(function () {}); } else el.video.pause();
        } catch (e) {}
    }
    function setPlayIcon(on) {
        // SVG elements have no `hidden` property — the attribute is what hides them.
        el.playIcon.toggleAttribute('hidden', on); el.pauseIcon.toggleAttribute('hidden', !on);
        el.play.setAttribute('aria-label', on ? T.pause : T.play);
    }
    function togglePlay() { if (P.on) { var keep = P.pos; stopPlayback(false); P.pos = keep; drawWave(); updateTime(); } else startPlay(P.pos || 0); }
    el.play.addEventListener('click', togglePlay);

    function applyAb(instant) {
        if (!fxGain) return;
        var t = ctx.currentTime, on = ab === 'fx';
        if (instant) { fxGain.gain.value = on ? 1 : 0; bypass.gain.value = on ? 0 : 1; return; }
        fxGain.gain.setTargetAtTime(on ? 1 : 0, t, 0.015); bypass.gain.setTargetAtTime(on ? 0 : 1, t, 0.015);
    }
    function setAb(v) {
        ab = v;
        el.abFx.classList.toggle('on', v === 'fx'); el.abOrig.classList.toggle('on', v !== 'fx');
        el.abFx.setAttribute('aria-checked', v === 'fx' ? 'true' : 'false'); el.abOrig.setAttribute('aria-checked', v !== 'fx' ? 'true' : 'false');
        applyAb(false);
        /* «Оригинал» with nothing playing used to toggle a class and stay silent —
           the page's top rage-click target. Choosing a side starts the sound. */
        if (!P.on && src) startPlay(P.pos || 0);
        ae('ab', { v: v });
    }
    el.abFx.addEventListener('click', function () { setAb('fx'); });
    el.abOrig.addEventListener('click', function () { setAb('orig'); });

    /* ── modes ────────────────────────────────────────────────────────── */
    function setMode(m) {
        if (m === mode) return;
        mode = m;
        el.modeFile.classList.toggle('on', m === 'file'); el.modeLive.classList.toggle('on', m === 'live');
        el.modeFile.setAttribute('aria-selected', m === 'file' ? 'true' : 'false'); el.modeLive.setAttribute('aria-selected', m === 'live' ? 'true' : 'false');
        el.srcPanel.hidden = m !== 'file'; el.livePanel.hidden = m !== 'live';
        if (m === 'live') {
            stopPlayback(false);
            el.exportPanel.hidden = true;
            if (src && src.isVideo) el.screen.classList.remove('has-video');
            el.video.hidden = true;
        } else {
            liveStop();
            el.exportPanel.hidden = !src;
            if (src && src.isVideo) { el.screen.classList.add('has-video'); el.video.hidden = false; }
        }
        kick();
        ae('mode', { m: m });
    }
    el.modeFile.addEventListener('click', function () { setMode('file'); });
    el.modeLive.addEventListener('click', function () { setMode('live'); });

    /* ── live: press to talk ──────────────────────────────────────────── */
    function liveNoiseLevel() {
        if (!L.noiseGain || !eng) return;
        var pl = eng.plan, v = (L.onAir && isFinite(pl.noise.db)) ? Math.pow(10, pl.noise.db / 20) : 0;
        try { L.noiseGain.gain.setTargetAtTime(v, ctx.currentTime, 0.02); } catch (e) { L.noiseGain.gain.value = v; }
        if (L.noiseType !== pl.noise.type) {
            L.noiseType = pl.noise.type;
            try { L.noiseGain.disconnect(); } catch (e) {}
            L.noiseGain.connect(pl.noise.type === 'cabin' ? eng.room : eng.rx);
            try { L.noise.buffer = noiseBufferFor(pl.noise.type); } catch (e) {}
        }
    }
    function noiseBufferFor(type) {
        var d = E.noiseLoop(type, ctx.sampleRate), b = ctx.createBuffer(1, d.length, ctx.sampleRate);
        b.copyToChannel(d, 0); return b;
    }
    async function liveStart() {
        if (L.ready || L.starting) return;
        L.starting = true;
        setText(el.liveStatus, T.live_requesting);
        el.ptt.disabled = true;
        try {
            await readyCtx();
            if (!window.MicManager) throw new Error('MicManager missing');
            if (!window._megaMic) window._megaMic = new window.MicManager({
                /* The context rides the mic's lifecycle: the iPhone permission sheet
                   suspends it, bindContext() wakes it (memory: 2026-07-31). */
                audioContext: function () { return ctx; },
                audioContextAnchorEl: el.liveStatus,
                fallbackAction: { label: T.use_file_mode_instead, handler: function () { setMode('file'); } }
            });
            /* echoCancellation ON: the phone's own speaker is the megaphone, and the
               canceller is what keeps it from howling. noiseSuppression OFF — the
               effect is the processing; AGC OFF — the level is ours. */
            L.stream = await window._megaMic.startRaw({ audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: false } });
            if (mode !== 'live') { window._megaMic.stop(); L.stream = null; return; }
            eng.setTrim(E.LIVE_TRIM_DB);
            L.node = ctx.createMediaStreamSource(L.stream);
            L.gate = ctx.createGain(); L.gate.gain.value = 0;
            L.inAn = ctx.createAnalyser(); L.inAn.fftSize = 1024;
            L.node.connect(L.inAn); L.node.connect(L.gate); L.gate.connect(eng.input);
            L.noise = ctx.createBufferSource(); L.noise.loop = true; L.noiseType = null;
            L.noiseGain = ctx.createGain(); L.noiseGain.gain.value = 0;
            L.noise.buffer = noiseBufferFor(eng.plan.noise.type);
            L.noise.connect(L.noiseGain);
            liveNoiseLevel();
            L.noise.start();
            L.ready = true; L.presses = 0; L.airMs = 0;
            el.ptt.classList.add('ready');
            pttLabels();
            el.liveRow.hidden = false;
            setText(el.liveStatus, '');
            fillSinks();
            watchLiveOutput();
            kick();
        } catch (e) {
            setText(el.liveStatus, '');
        } finally {
            L.starting = false;
            el.ptt.disabled = false;
        }
    }
    function liveStop() {
        if (L.rec) liveRecStop();
        if (L.onAir) setOnAir(false, true);
        if (L.ready) ae('live_session', { presses: L.presses, air_s: Math.round(L.airMs / 1000), p: params.preset });
        clearTimeout(liveWatch);
        try { if (L.noise) L.noise.stop(); } catch (e) {}
        [L.node, L.gate, L.noise, L.noiseGain, L.inAn].forEach(function (n) { try { if (n) n.disconnect(); } catch (e) {} });
        if (window._megaMic && L.stream) { try { window._megaMic.stop(); } catch (e) {} }
        L.stream = L.node = L.gate = L.noise = L.noiseGain = L.inAn = null;
        L.ready = false; L.onAir = false;
        el.ptt.classList.remove('ready', 'on');
        el.ptt.setAttribute('aria-pressed', 'false');
        setText(el.pttLabel, T.live_start); setText(el.pttSub, T.live_start_sub);
        el.liveRow.hidden = true; el.air.hidden = true;
        if (el.ring) el.ring.style.setProperty('--lvl', '0');
        if (src && eng) eng.setTrim(src.trimDb);
    }
    function pttLabels() {
        setText(el.pttLabel, L.onAir ? T.on_air : (T.ptt_label || T.off_air));
        setText(el.pttSub, L.onAir ? T.ptt_sub_on : T.ptt_sub_idle);
    }
    function setOnAir(on, quiet) {
        if (!L.ready || on === L.onAir) return;
        L.onAir = on;
        var t = ctx.currentTime;
        try { L.gate.gain.setTargetAtTime(on ? 1 : 0, t, 0.008); } catch (e) { L.gate.gain.value = on ? 1 : 0; }
        if (on) {
            L.presses++; L.airT0 = performance.now();
            if (params.ptt) E.fire(ctx, eng, 'click', t + 0.005);
            liveNoiseLevel();
        } else {
            L.airMs += performance.now() - L.airT0;
            var at = t + 0.03;
            if (!quiet && params.beep) { E.fire(ctx, eng, 'beep', at); at += 0.2; }
            if (!quiet && params.ptt) { E.fire(ctx, eng, 'squelch', at); at += 0.3; }
            /* The channel's hiss closes with the squelch, not before it. */
            var stopAt = Math.max(0, (at - ctx.currentTime) * 1000);
            setTimeout(function () { if (!L.onAir) liveNoiseLevel(); }, stopAt);
            if (!(params.beep || params.ptt) || quiet) liveNoiseLevel();
        }
        el.ptt.classList.toggle('on', on);
        el.ptt.setAttribute('aria-pressed', on ? 'true' : 'false');
        el.air.hidden = !on;
        pttLabels();
    }
    /* One owner for the press (3d-audio's A/B pattern, OneGestureOneActionTest):
       holding is momentary — talk while held, silence on release; a quick tap
       latches the channel open (and the next tap closes it); the keyboard's
       click toggles. The click that follows a pointer press belongs to the press. */
    var HOLD_MS = 280, press = null, pressEndedAt = -1e9;
    el.ptt.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    el.ptt.addEventListener('pointerdown', function (e) {
        if (e.button !== 0 || !L.ready) return;
        try { el.ptt.setPointerCapture(e.pointerId); } catch (x) {}
        press = { t: e.timeStamp, was: L.onAir };
        setOnAir(true);
        e.preventDefault();
    });
    function pressEnd(e, ev) {
        if (!press) return;
        var held = ev !== 'pointerup' || e.timeStamp - press.t >= HOLD_MS;
        setOnAir(held ? press.was : !press.was);
        press = null; pressEndedAt = e.timeStamp;
    }
    el.ptt.addEventListener('pointerup', function (e) { pressEnd(e, 'pointerup'); });
    el.ptt.addEventListener('pointercancel', function (e) { pressEnd(e, 'pointercancel'); });
    el.ptt.addEventListener('click', function (e) {
        if (!L.ready) { liveStart(); return; }
        if (e.detail !== 0 && e.timeStamp - pressEndedAt < 1000) { e.preventDefault(); return; }
        setOnAir(!L.onAir);
    });
    el.liveOff.addEventListener('click', function () { liveStop(); });
    el.gong.addEventListener('click', function () {
        if (!L.ready) return;
        var P0 = E.PRESETS[params.preset];
        E.fire(ctx, eng, 'chime_' + (P0.chime || 'station'));
        ae('live_gong');
    });
    el.beep.addEventListener('click', function () { if (L.ready) { E.fire(ctx, eng, 'beep'); ae('live_beep'); } });

    var levelBuf = null;
    function tickLive() {
        if (!L.inAn) return;
        if (!levelBuf || levelBuf.length !== L.inAn.fftSize) levelBuf = new Float32Array(L.inAn.fftSize);
        L.inAn.getFloatTimeDomainData(levelBuf);
        var e = 0; for (var i = 0; i < levelBuf.length; i++) e += levelBuf[i] * levelBuf[i];
        var db = 10 * Math.log10(e / levelBuf.length + 1e-12);
        var v = Math.round(Math.max(0, Math.min(1, (db + 60) / 50)) * 50) / 50;   // 50 steps: a new frame only when the ring moves
        if (v !== tickLive.v) { tickLive.v = v; el.ring.style.setProperty('--lvl', String(v)); }
    }
    /* The mic path's only success signal: one event ~4 s in, peak at the output
       while on air (0 = nothing reached the speaker). Kept from the old page. */
    var liveWatch = 0;
    function watchLiveOutput() {
        clearTimeout(liveWatch);
        var peak = 0, t0 = Date.now(), d = null;
        var sample = function () {
            if (!L.ready || !analyser) return;
            if (!d) d = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(d);
            for (var i = 0; i < d.length; i++) if (d[i] > peak) peak = d[i];
            if (Date.now() - t0 < 6000) { liveWatch = setTimeout(sample, 250); return; }
            ae('mic_monitor', { peak: peak, silent: peak <= 2 ? 1 : 0, ctx: (ctx && ctx.state) || 'none', air: L.presses ? 1 : 0 });
        };
        liveWatch = setTimeout(sample, 250);
    }

    /* Output device — route the megaphone to a Bluetooth speaker (or a virtual
       cable that another app listens to). AudioContext.setSinkId: Chromium 110+;
       elsewhere the select stays hidden and the system output is used. */
    async function fillSinks() {
        if (!el.sink || typeof (ctx && ctx.setSinkId) !== 'function' || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
        try {
            var list = (await navigator.mediaDevices.enumerateDevices()).filter(function (d) { return d.kind === 'audiooutput'; });
            if (list.length < 2) return;
            el.sink.innerHTML = '';
            var o0 = document.createElement('option'); o0.value = ''; o0.textContent = T.output_default; el.sink.appendChild(o0);
            list.forEach(function (d) { if (d.deviceId === 'default' || d.deviceId === 'communications') return; var o = document.createElement('option'); o.value = d.deviceId; o.textContent = d.label || d.deviceId.slice(0, 8); el.sink.appendChild(o); });
            el.sink.hidden = false;
        } catch (e) {}
    }
    el.sink.addEventListener('change', function () {
        if (!ctx || !ctx.setSinkId) return;
        ctx.setSinkId(el.sink.value || '').then(function () { ae('sink', { custom: el.sink.value ? 1 : 0 }); }).catch(function () { el.sink.value = ''; });
    });

    /* Live recording — the RAW microphone, so the take can be re-tuned with any
       sound afterwards and saved in any format: it lands as the source. */
    function liveRecStop() {
        if (!L.rec) return;
        try { L.rec.stop(); } catch (e) {}
        clearInterval(L.recTimer);
        el.liveRec.classList.remove('rec');
        setText(el.liveRecTxt, T.live_record);
    }
    el.liveRec.addEventListener('click', function () {
        if (!L.ready) return;
        if (L.rec) { liveRecStop(); return; }
        var mime = window.__pickPlayableRecMime ? window.__pickPlayableRecMime(['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) : '';
        var rec;
        try { rec = mime ? new MediaRecorder(L.stream, { mimeType: mime }) : new MediaRecorder(L.stream); } catch (e) { toast(T.err_browser); return; }
        L.rec = rec; L.recChunks = []; L.recT0 = Date.now();
        rec.ondataavailable = function (ev) { if (ev.data && ev.data.size) L.recChunks.push(ev.data); };
        rec.onstop = function () {
            var type = (rec.mimeType || mime || 'audio/webm').split(';')[0];
            var blob = new Blob(L.recChunks, { type: type });
            L.rec = null;
            if (!blob.size) return;
            var f = new File([blob], (T.live_rec_name || 'recording') + '.' + (/mp4/.test(type) ? 'm4a' : 'webm'), { type: type });
            ae('live_record', { sec: Math.round((Date.now() - L.recT0) / 1000) });
            setMode('file');
            if (el.upload.__uploaderInjectFile) el.upload.__uploaderInjectFile(f, 'live_record'); else loadSource(f, 'live_record');
            setStatus(T.live_recorded);
        };
        rec.start(250);
        el.liveRec.classList.add('rec');
        var tickRec = function () { setText(el.liveRecTxt, String(T.live_record_stop || '').replace(':t', fmtTime((Date.now() - L.recT0) / 1000))); };
        tickRec(); L.recTimer = setInterval(tickRec, 500);
    });

    /* ── export ───────────────────────────────────────────────────────── */
    function fmtLabel(f) { return f === 'video' ? T.fmt_video : ((CFG.formats[f] && CFG.formats[f].name) || f.toUpperCase()); }
    function setupFormats() {
        var vchip = el.fmts.querySelector('[data-fmt="video"]');
        vchip.hidden = !(src && src.isVideo);
        if (src && src.isVideo && !setupFormats.userPickedForVideo) fmt = 'video';
        if (fmt === 'video' && !(src && src.isVideo)) fmt = settingsLoad('fmt', 'mp3') === 'video' ? 'mp3' : settingsLoad('fmt', 'mp3');
        if (fmt !== 'video' && !CFG.formats[fmt]) fmt = 'mp3';
        renderFormatUi();
    }
    function renderFormatUi() {
        var chips = el.fmts.querySelectorAll('[data-fmt]');
        for (var i = 0; i < chips.length; i++) {
            var on = chips[i].getAttribute('data-fmt') === fmt;
            chips[i].classList.toggle('on', on); chips[i].setAttribute('aria-checked', on ? 'true' : 'false');
        }
        var f = CFG.formats[fmt];
        el.qMp3.hidden = fmt !== 'mp3';
        var lossyOther = fmt !== 'mp3' && fmt !== 'video' && f && !f.lossless;
        el.qOther.hidden = !lossyOther;
        if (lossyOther) {
            el.kbps.innerHTML = '';
            var list = f.kbps || [128], want = kbpsChoice[fmt] || (list.indexOf(128) >= 0 ? 128 : list[Math.floor(list.length / 2)]);
            list.forEach(function (k) {
                var b = document.createElement('button');
                b.type = 'button'; b.className = 'tc-chip' + (k === want ? ' on active' : ''); b.setAttribute('role', 'radio');
                b.setAttribute('aria-checked', k === want ? 'true' : 'false'); b.setAttribute('data-kbps', String(k)); b.textContent = String(k);
                el.kbps.appendChild(b);
            });
        }
        el.kept.hidden = fmt !== 'video';
        setText(el.dlTxt, String(T.download_fmt || '').replace(':fmt', fmtLabel(fmt)));
    }
    el.fmts.addEventListener('click', function (e) {
        var b = e.target.closest('[data-fmt]');
        if (!b || X.busy) return;
        fmt = b.getAttribute('data-fmt');
        if (src && src.isVideo) setupFormats.userPickedForVideo = true;
        if (fmt !== 'video') settingsSave('fmt', fmt);
        renderFormatUi();
        if (fmt !== 'mp3' && fmt !== 'wav') warmFFmpeg();
        ae('format', { fmt: fmt });
    });
    el.kbps.addEventListener('click', function (e) {
        var b = e.target.closest('[data-kbps]');
        if (!b || b.disabled || b.getAttribute('aria-disabled') === 'true') return;
        kbpsChoice[fmt] = +b.getAttribute('data-kbps'); settingsSave('kbps', kbpsChoice);
        renderFormatUi();
    });
    function currentKbps() {
        if (fmt === 'mp3') { try { return window.__mp3Quality ? window.__mp3Quality.value('mgMp3Q', 192) : 192; } catch (e) { return 192; } }
        var on = el.kbps.querySelector('.tc-chip.on');
        return on ? +on.getAttribute('data-kbps') : 128;
    }
    var ffWarm = null;
    function warmFFmpeg() {
        if (ffWarm || !window.FFmpegProcessor || !window.FFmpegProcessor.init) return;
        try { ffWarm = Promise.resolve(window.FFmpegProcessor.init()).catch(function () { ffWarm = null; }); } catch (e) {}
    }
    var statusRow = $('mgStatusRow');
    function setStatus(s) { setText(el.status, s || ''); if (statusRow) statusRow.hidden = !s && !X.busy; }
    function setProg(f) {
        el.prog.hidden = f == null;
        if (f != null) pxWrite(el.progBar, 'width', Math.max(0, Math.min(100, f * 100)));
    }
    function setBusy(on) {
        X.busy = on;
        el.dl.disabled = on; el.batch.disabled = on;
        el.cancel.hidden = !on;
        if (statusRow) statusRow.hidden = !on && !el.status.textContent;
        if (!on) { setProg(null); }
    }
    window.addEventListener('ffmpeg-status', function (ev) {
        if (!X.busy || !ev || !ev.detail) return;
        if (ev.detail.phase === 'downloading') setStatus(T.engine_loading);
    });

    function baseName(name) { return String(name || 'audio').replace(/\.[^.]+$/, ''); }
    function outName(name, ext) {
        var tag = T['preset_' + params.preset] || params.preset;
        try { if (window.__outputName && window.__outputName.join) { var n = window.__outputName.join([baseName(name), tag], ext); if (n) return n; } } catch (e) {}
        return baseName(name) + '-' + tag + '.' + ext;
    }
    function extOf(f) {
        var cfg = window.__FFMPEG_CONFIG__;
        return (cfg && cfg.extMap && cfg.extMap[f]) || f;
    }
    function onRenderProgress(span) { return function (f) { setProg(span[0] + f * (span[1] - span[0])); setStatus(String(T.rendering || '').replace(':p', String(Math.round((span[0] + f * (span[1] - span[0])) * 100)))); }; }

    /* Render the source through the engine into a WAV (disk-backed past the sink's
       spill line) — the one path every export starts from, except MP3. */
    async function renderWav(s, opts) {
        var sr = s.buffer.sampleRate, ch = opts.channels || (E.plan(params, sr).stereo ? 2 : 1);
        var sink = await window.OutputSink.create({ mime: 'audio/wav', name: 'megaphone' });
        var w = window.OutputSink.wavWriter(sink, sr, ch, 16);
        try {
            await E.render(s.buffer, { params: params, trimDb: s.trimDb, extend: opts.extend !== false, channels: ch, signal: opts.signal,
                onChunk: function (a) { return w.writeChunk(a); }, onProgress: opts.onProgress });
            return await w.finalize();
        } catch (e) { try { await sink.cleanup(); } catch (x) {} throw e; }
    }
    async function renderMp3(s, kbps, opts) {
        var sr = s.buffer.sampleRate, ch = E.plan(params, sr).stereo ? 2 : 1;
        if (window.ensureAudioPipeline) await window.ensureAudioPipeline();
        if (!window.MP3Encoder || !window.MP3Encoder.encodeStream) return null;
        var enc;
        try { enc = await window.MP3Encoder.encodeStream({ sampleRate: sr, channels: ch, bitrate: kbps, slug: SLUG }); }
        catch (e) { return null; }                     // a rate lame does not take: the FFmpeg lane resamples
        try {
            await E.render(s.buffer, { params: params, trimDb: s.trimDb, extend: true, channels: ch, signal: opts.signal,
                onChunk: function (a) { return enc.feed(a.map(function (x) { return x.slice(); })); }, onProgress: opts.onProgress });
            var fin = await enc.finish();                // {blob, source, samplesIn}
            return fin && fin.blob ? fin.blob : fin;
        } catch (e) { try { enc.abort(); } catch (x) {} throw e; }
    }
    async function ffmpegReady() {
        var FP = window.FFmpegProcessor;
        if (!FP) throw new Error('FFmpegProcessor missing');
        if (FP.init) { var ok = await FP.init(); if (ok === false) throw new Error('ffmpeg init'); }
        return FP;
    }
    async function encodeVia(fp, wav, f, kbps) {
        var ext = extOf(f);
        var args = (fp.getCodecArgs ? fp.getCodecArgs(f, kbps) : []);
        var r = await fp.process(new File([wav], 'input.wav', { type: 'audio/wav' }), args, 'output.' + ext, 'input.wav');
        return { blob: (r && r.blob) || r, ext: ext };
    }
    function raceFor(fp) {
        return function (job) {
            return new Promise(function (resolve, reject) {
                var done = false, stall = 0;
                function end(fn, v) { if (done) return; done = true; clearTimeout(stall); fn(v); }
                function arm() { clearTimeout(stall); stall = setTimeout(function () { end(reject, new Error('ffmpeg stalled')); }, 60000); }
                try { if (typeof fp.onProgress === 'function') fp.onProgress(function () { arm(); }); } catch (e) {}
                arm();
                Promise.resolve(job).then(function (v) { end(resolve, v); }, function (e) { end(reject, e); });
            });
        };
    }
    /* A video keeps its picture: only the sound track is replaced. mp4/mov — the
       splice (picture stays byte references to the source, any size); others —
       the copy mux into the same container. */
    async function muxVideo(s, wav) {
        var fp = await ffmpegReady(), file = s.file, out = null, lane = 'splice';
        var isWebm = /webm/i.test(file.type || '') || /\.webm$/i.test(file.name || '');
        var wf = new File([wav], 'megaphone.wav', { type: 'audio/wav' });
        if (!isWebm && window.SoundtrackSplice && window.SoundtrackSplice.capable(file)) {
            try {
                var sp = await window.SoundtrackSplice.spliceDecoded(file, wf, { P: fp, kbps: 192, race: raceFor(fp),
                    mime: file.type || (/\.mov$/i.test(file.name || '') ? 'video/quicktime' : 'video/mp4') });
                out = { blob: sp.blob, ext: /\.mov$/i.test(file.name || '') ? 'mov' : 'mp4' };
            } catch (se) { if (!(se && se.remuxReason)) throw se; lane = 'copy'; }
        } else lane = 'copy';
        if (!out) {
            var ceil = (window.SoundtrackSplice && window.SoundtrackSplice.classicCeiling) ? window.SoundtrackSplice.classicCeiling() : 1073741824;
            if ((file.size || 0) > ceil) throw Object.assign(new Error('copy mux over its ceiling'), { mgUser: T.err_video });
            var ext = isWebm ? 'webm' : 'mp4';
            var codec = isWebm ? ['-c:a', 'libopus', '-b:a', '128k'] : ['-c:a', 'aac', '-b:a', '192k'];
            var sh = (window.SoundtrackSplice && window.SoundtrackSplice.shiftDecoded) ? await window.SoundtrackSplice.shiftDecoded(file, wf, fp) : { wav: wf };
            var fn = fp.processMultiStreaming || fp.processMulti;
            var res = await raceFor(fp)(fn.call(fp, [file, sh.wav], ['-map', '0:v:0', '-map', '1:a:0', '-c:v', 'copy'].concat(codec).concat(['-shortest']), 'output.' + ext));
            out = { blob: res && res.blob, ext: ext };
        }
        ae('video_lane', { lane: lane, mb: Math.round((file.size || 0) / 1048576) });
        return out;
    }

    async function exportOne(s, f, kbps, signal, span) {
        span = span || [0, 1];
        var mid = span[0] + (span[1] - span[0]) * 0.8;
        if (f === 'video') {
            var w = await renderWav(s, { extend: false, channels: 2, signal: signal, onProgress: onRenderProgress([span[0], mid]) });
            setStatus(T.muxing); setProg(mid);
            return await muxVideo(s, w);
        }
        if (f === 'mp3') {
            var m = await renderMp3(s, kbps, { signal: signal, onProgress: onRenderProgress(span) });
            if (m) return { blob: m, ext: 'mp3' };
        }
        var wav = await renderWav(s, { extend: true, signal: signal, onProgress: onRenderProgress(f === 'wav' ? span : [span[0], mid]) });
        if (f === 'wav') return { blob: wav, ext: 'wav' };
        setStatus(T.encoding); setProg(mid);
        var fp = await ffmpegReady();
        return await encodeVia(fp, wav, f, kbps);
    }

    el.dl.addEventListener('click', async function () {
        if (!src || X.busy) return;
        var token = null;
        if (window.__toolLimit) { token = window.__toolLimit.begin(SLUG, src.key); if (!token) return; }
        X.token = token; X.ac = new AbortController();
        stopPlayback(false);
        setBusy(true); setProg(0); setStatus(String(T.rendering || '').replace(':p', '0'));
        var t0 = Date.now(), f = fmt, kbps = currentKbps(), sig = X.ac.signal;
        try {
            var out = await exportOne(src, f, kbps, sig);
            if (sig.aborted) throw Object.assign(new Error('cancelled'), { name: 'AbortError' });
            if (!out || !out.blob || !out.blob.size) throw new Error('empty output');
            setProg(1); setStatus(T.done);
            var name = outName(src.name, out.ext);
            var ok = window.__downloadFile ? await window.__downloadFile(out.blob, name) : false;
            if (ok !== false) {
                if (X.token) { X.token.commit(); X.token = null; }
                try { if (window.__aeToolComplete) window.__aeToolComplete({ fmt: f, p: params.preset, custom: E.matchesPreset(params) ? 0 : 1, video: src.isVideo ? 1 : 0, sec: Math.round(src.buffer.duration), ms: Date.now() - t0, kbps: kbps, demo: src.demo ? 1 : 0 }); } catch (e) {}
            } else if (X.token) { X.token.abort(); X.token = null; }
        } catch (err) {
            if (X.token) { X.token.abort(); X.token = null; }
            if (isCancel(err, sig)) setStatus(T.cancelled);
            else {
                setStatus('');
                var say = (err && err.mgUser) || (f === 'video' ? T.err_video : T.err_export);
                try { if (window.__handleError) window.__handleError(err, 'megaphone:export', { say: say }); else toast(say); } catch (e) { toast(say); }
                ae('export_failed', { fmt: f, msg: String((err && err.message) || err).slice(0, 80) });
            }
        } finally {
            setBusy(false); X.ac = null;
        }
    });
    el.cancel.addEventListener('click', function () {
        if (!X.busy) return;
        if (X.token) { X.token.abort(); X.token = null; }
        try { if (X.ac) X.ac.abort(); } catch (e) {}
        try { if (window.FFmpegProcessor && window.FFmpegProcessor.cancel) window.FFmpegProcessor.cancel(); else if (window.FFmpegProcessor && window.FFmpegProcessor.abort) window.FFmpegProcessor.abort(); } catch (e) {}
    });

    /* ── a batch with the same sound ──────────────────────────────────── */
    var batchInput = document.createElement('input');
    batchInput.type = 'file'; batchInput.multiple = true; batchInput.hidden = true;
    batchInput.accept = CFG.accept || 'audio/*,video/*';
    document.body.appendChild(batchInput);
    el.batch.addEventListener('click', function () { if (!X.busy) { batchInput.value = ''; batchInput.click(); } });
    batchInput.addEventListener('change', async function () {
        var files = Array.prototype.slice.call(batchInput.files || []);
        if (!files.length || X.busy) return;
        var f = fmt === 'video' ? 'mp3' : fmt, kbps = currentKbps();
        var keys = files.map(function (x) { try { return window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(x) : null; } catch (e) { return null; } });
        var token = null;
        if (window.__toolLimit) { token = window.__toolLimit.begin(SLUG, keys); if (!token) return; }
        X.token = token; X.ac = new AbortController();
        var sig = X.ac.signal, done = 0, batch = null, failed = 0;
        setBusy(true); setProg(0);
        try {
            if (!window.__batchDeliver) throw new Error('batch-deliver missing');
            batch = await window.__batchDeliver.openBatch({ zipName: outName('megaphone', 'zip'), slug: SLUG, total: files.length });
            if (!batch) throw Object.assign(new Error('declined'), { name: 'AbortError' });
            for (var i = 0; i < files.length; i++) {
                if (sig.aborted) throw Object.assign(new Error('cancelled'), { name: 'AbortError' });
                setStatus(String(T.batch_progress || '').replace(':n', String(i + 1)).replace(':total', String(files.length)));
                try {
                    getCtx();
                    var dec = await window.AudioDecoderRobust.robust(files[i], ctx, { slug: SLUG });
                    var chans = []; for (var c = 0; c < dec.buffer.numberOfChannels; c++) chans.push(dec.buffer.getChannelData(c));
                    var lvl = E.speechLevelDb(chans, dec.buffer.sampleRate);
                    if (lvl <= -59.9) { failed++; continue; }
                    var s = { file: files[i], buffer: dec.buffer, isVideo: false, name: files[i].name, trimDb: E.trimFor(lvl) };
                    var out = await exportOne(s, f, kbps, sig, [i / files.length, (i + 1) / files.length]);
                    await batch.write(outName(files[i].name, out.ext), out.blob);
                    done++;
                } catch (e1) {
                    if (isCancel(e1, sig)) throw e1;
                    failed++;
                }
            }
            if (!done) throw Object.assign(new Error('nothing done'), { mgUser: T.batch_none });
            await batch.finish();
            setProg(1);
            setStatus(String(T.batch_done || '').replace(':n', String(done)));
            if (X.token) { X.token.commit(); X.token = null; }
            ae('batch', { n: files.length, done: done, failed: failed, fmt: f, mode: batch.mode });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ batch: done, fmt: f, p: params.preset }); } catch (e) {}
        } catch (err) {
            if (X.token) { X.token.abort(); X.token = null; }
            if (isCancel(err, sig)) setStatus(T.cancelled);
            else {
                setStatus('');
                var say = (err && err.mgUser) || T.err_export;
                try { if (window.__handleError) window.__handleError(err, 'megaphone:batch', { say: say }); else toast(say); } catch (e) { toast(say); }
            }
        } finally { setBusy(false); X.ac = null; }
    });

    /* ── start ────────────────────────────────────────────────────────── */
    document.addEventListener('visibilitychange', function () { if (document.hidden && L.onAir && !press) setOnAir(false, true); });
    window.addEventListener('pagehide', function () { liveStop(); });
    renderFormatUi();
    applyParams();
    drawScope(performance.now());
}

function start() {
    var tries = 0;
    (function wait() {
        if (window.MegaphoneEngine) { boot(window.MegaphoneEngine); return; }
        if (++tries > 100) return;                         // engine never arrived: the noscript-style page stays
        setTimeout(wait, 100);
    })();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
